import { Component, ViewEncapsulation, input, inject } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { NgClass, NgTemplateOutlet } from '@angular/common';

import { ErrorMessages, ApFormErrorsComponent } from '@ap/form/errors';
import { ApContentService } from '@ap/content';
import { HelpText, HelpTextPosition } from '@ap/form';

import content from './radio-group.content.json';

const uniqueId = `radio-group-${Math.round(Math.random() * 10000000)}`;

/**
 * @ngdoc component
 * @module ApRadioModule
 * @name ApRadioGroupComponent
 * @description A component for a single radio group including a label.
 */
@Component({
    selector: 'ap-radio-group',
    templateUrl: './radio-group.component.html',
    styleUrls: ['./radio-group.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    imports: [NgClass, ApFormErrorsComponent, NgTemplateOutlet]
})
export class ApRadioGroupComponent implements HelpText {
    protected readonly contentService = inject(ApContentService);

    /**
     * Description of the `fieldset` rendered as `legend`
     */
    public readonly description = input.required<string>();

    /**
     * Visually hides the description legend
     */
    public readonly descriptionHidden = input(false);

    /**
     * Optional control input. Passed on to radio buttons
     */
    public readonly control = input<FormControl<string | number>>();

    /**
     * Error message object or array of strings.
     * If array of strings: First string is always shown on error
     * If error message object: Key that matches current error is shown.
     */
    public readonly messages = input<ErrorMessages | string[]>();

    /**
     * Help text shown under field. Disappears if errors are present
     */
    public readonly helpText = input<string>();

    /**
     * Help text position, either below (default) or above the input field.
     */
    public readonly helpTextPosition = input<HelpTextPosition>(HelpTextPosition.BELOW);

    /**
     * Expose `HelpTextPosition` enum for template
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention
    protected readonly HelpTextPosition = HelpTextPosition;

    /**
     * Are the radio buttons inline?
     * Defaults to false
     */
    public readonly inline = input(false);

    /**
     * Json object that contains default content messages from form element
     */
    protected content: { [key: string]: any };

    /**
     * Unique id of field that can be used to map aria fields
     */
    protected uniqueId = uniqueId;
    protected helpTextId = `${uniqueId}-helptext`;
    protected errorsId = `${uniqueId}-errors`;

    constructor() {
        this.contentService.addContentItems(content);
        this.content = content;
    }

    /**
     * Check if the radio group is required
     */
    protected get isRequired(): boolean {
        const control = this.control();
        if (!control) {
            return false;
        }
        return control.hasValidator(Validators.required);
    }

    /**
     * Check if component is in error state
     */
    public get isErrorState(): boolean {
        const control = this.control();
        if (!control) {
            return false;
        }
        return control.invalid && control.dirty && control.touched;
    }

    /**
     * Aria described by contains conditional fields that describe the fieldset
     */
    public get ariaDescribedBy(): string {
        return [this.errorsId, ...(this.helpText() ? [this.helpTextId] : [])].join(' ');
    }
}
